import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createElementVNode as _createElementVNode, renderSlot as _renderSlot, withCtx as _withCtx, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, normalizeStyle as _normalizeStyle, vShow as _vShow, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-5fa28fa8"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "bg-wild-sand relative" }
const _hoisted_2 = { class: "w-full h-full aspect-square flex justify-center items-center py-48 px-24 xl:px-100 xl:py-130" }
const _hoisted_3 = {
  key: 0,
  class: "absolute inset-x2 z-1"
}
const _hoisted_4 = { class: "absolute left-0" }
const _hoisted_5 = { class: "bg-wild-sand relative" }
const _hoisted_6 = { class: "w-full h-full aspect-square flex justify-center items-center" }
const _hoisted_7 = { class: "bg-wild-sand relative" }
const _hoisted_8 = { class: "w-full h-full aspect-square flex justify-center items-center" }
const _hoisted_9 = { class: "bg-wild-sand relative" }
const _hoisted_10 = { class: "w-full h-full aspect-square flex justify-center items-center" }
const _hoisted_11 = { class: "relative flex justify-center" }
const _hoisted_12 = { class: "thumb-image" }
const _hoisted_13 = {
  key: 0,
  class: "relative flex justify-center"
}
const _hoisted_14 = { class: "thumb-image" }
const _hoisted_15 = ["src"]
const _hoisted_16 = { class: "thumb-image" }
const _hoisted_17 = { class: "thumb-image bg-wild-sand" }
const _hoisted_18 = { class: "thumb-image bg-wild-sand" }
const _hoisted_19 = {
  key: 0,
  class: "w-full relative"
}
const _hoisted_20 = { class: "xy-center z-1 bg-black-40 rounded-md leading-16px pl-10 pr-7 pb-4 xl:pt-2 xl:pb-2" }

import { ref, computed } from 'vue';
import { Swiper as ISwiper, SwiperOptions } from 'swiper';
import { ProductImageWithCacheBust } from '@/shared/product/details/useProductImages';
import useBooleanToggle from '@/shared/useBooleanToggle';
import ProductImage from '@/shared/media/ProductImage.vue';
import breakpointsState from '@/core/responsive/breakpoints/breakpoints-state.observable';
import { ModuleConfigurationImage, ProductDetailCoverMaterial, ProductLabel, ProductLabelType } from '@/types/productServerApi';
import useSwiperSlideDynamicUnderline from './useSwiperSlideDynamicUnderline';
import VisualizationOverlay from '@/shared/product/details/ui/visualization/VisualizationOverlay.vue';
import DynamicUnderline from '@/shared/navigation/DynamicUnderline.vue';
import VisualizationHighlights from '@/shared/product/details/ui/visualization/VisualizationHighlights.vue';
import { CollageTileLayoutData } from '@/shared/content/blocks/collage-layouts/collage-layout.store';
import ModuleImage from '@/shared/product/details/ui/module-configurator/ModuleImage.vue';
import MediaHandler from '@/shared/content/blocks/helpers/MediaHandler.vue';
import constants from '@/shared/config/constants';
import ProductSplashLabelProductSpot from '@/shared/product/splash/ui/ProductSplashLabelProductSpot.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductDetailVisualization',
  props: {
    productImages: null,
    environmentalImages: null,
    coverMaterial: null,
    damageImages: null,
    configuratorImages: null,
    alt: null,
    productLabels: null
  },
  setup(__props: any) {

const props = __props as {
    productImages: ProductImageWithCacheBust[],
    environmentalImages?: CollageTileLayoutData[],
    coverMaterial?: ProductDetailCoverMaterial,
    damageImages?: string[],
    configuratorImages?: ModuleConfigurationImage[],
    alt?: string,
    productLabels?: ProductLabel[],
}



const { isToggled, toggle } = useBooleanToggle();
const overlayInitialSlide = ref();
const openOverlay = (currentIndex: number) => {
    overlayInitialSlide.value = currentIndex;
    toggle();
};

const configuratorIndexOffset = computed(() => props.productImages.length);
const damageImagesIndexOffset = computed(() => configuratorIndexOffset.value + (props.configuratorImages?.length ?? 0) + (props.coverMaterial?.image ? 1 : 0));
const environmentalImagesIndexOffset = computed(() => damageImagesIndexOffset.value + (props.damageImages?.length ?? 0));

const productSpotLabel = computed(() => props.productLabels?.find((label) => label.type === ProductLabelType.ProductSpot));

const thumbSwiper = ref<ISwiper | null>(null);
const swiperOptions = computed(() => {
    const options = {} as SwiperOptions;
    if (isMobile.value) {
        options.spaceBetween = 10;
    } else {
        options.spaceBetween = 12;
        options.pagination = false;

        if (isToggled) {
            options.keyboard = {
                enabled: false,
            };
        }
    }
    return options;
});

const setThumbSwiper = (swiper: ISwiper) => {
    thumbSwiper.value = swiper;
};

const isMobile = computed(() => breakpointsState.activeBreakpoint === 'xs');
const numberOfImages = computed(() => props.productImages.length + (props.configuratorImages?.length ?? 0) + (props.coverMaterial?.image ? 1 : 0) + (props.damageImages?.length ?? 0) + (props.environmentalImages?.length ?? 0));
const showThumbnails = computed(() => !isMobile.value && numberOfImages.value > 1);

const thumbSwiperOptions = computed(() => {
    const isSingleSlide = numberOfImages.value < 2;
    const slidesPerView = isSingleSlide ? 1 : 'auto';
    const slidesPerGroup = isSingleSlide ? 1 : 5;
    return {
        watchSlidesProgress: true,
        pagination: false,
        watchSlidesVisibility: true,
        watchOverflow: true,
        slidesPerView,
        slidesPerGroup,
        navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
            disabledClass: 'disabled_swiper_button',
        },
    };
});

const { removeUnderline, setUnderlineAnimation, targetWidth, targetXPosition, setActiveIndex } = useSwiperSlideDynamicUnderline(thumbSwiper);

const thumbX = ref(0);

const handleThumbChange = (e) => {
    thumbX.value = e.translate;

    if (e.isBeginning) {
        thumbX.value = 0;
    }
};

const handleSlideChange = (swiper: ISwiper) => {
    setActiveIndex(swiper.activeIndex);
};

return (_ctx: any,_cache: any) => {
  const _component_SwiperSlide = _resolveComponent("SwiperSlide")!
  const _component_ResponsiveImage = _resolveComponent("ResponsiveImage")!
  const _component_Swiper = _resolveComponent("Swiper")!
  const _component_c_icon = _resolveComponent("c-icon")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_Swiper, {
      thumbs: thumbSwiper.value,
      "free-mode": false,
      "hide-arrows": true,
      options: _unref(swiperOptions),
      class: "product-detail-swiper",
      onSlideChange: handleSlideChange
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.productImages, (image, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: image.maybeAngleImageSku + index,
            class: "cursor-zoom-in",
            onClick: ($event: any) => (openOverlay(index))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(ProductImage, {
                    class: "w-full",
                    sku: image.maybeAngleImageSku,
                    "aspect-ratio": 4/3,
                    lazy: false,
                    "width-on-screen": {xs: 100, md: 50},
                    alt: __props.alt,
                    "cache-bust": image.version
                  }, null, 8, ["sku", "alt", "cache-bust"])
                ]),
                _createVNode(VisualizationHighlights, null, {
                  default: _withCtx(() => [
                    _renderSlot(_ctx.$slots, "default")
                  ]),
                  _: 3
                }),
                (__props.productLabels)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createElementVNode("div", _hoisted_4, [
                        _createVNode(ProductSplashLabelProductSpot, {
                          class: "-ml-x2",
                          label: _unref(productSpotLabel)
                        }, null, 8, ["label"])
                      ])
                    ]))
                  : _createCommentVNode("", true)
              ])
            ]),
            _: 2
          }, 1032, ["onClick"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.configuratorImages, (configuratorImage, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: index,
            class: "cursor-zoom-in",
            onClick: ($event: any) => (openOverlay(_unref(configuratorIndexOffset) + index))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(ModuleImage, {
                    "configurator-image": configuratorImage.content
                  }, null, 8, ["configurator-image"])
                ])
              ])
            ]),
            _: 2
          }, 1032, ["onClick"]))
        }), 128)),
        (__props.coverMaterial?.imageName)
          ? (_openBlock(), _createBlock(_component_SwiperSlide, {
              key: "coverMaterial",
              class: "cursor-zoom-in",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (openOverlay(__props.productImages.length + (__props.configuratorImages?.length || 0))))
            }, {
              default: _withCtx(() => [
                _createVNode(_component_ResponsiveImage, {
                  "is-product-image": "",
                  "image-url": __props.coverMaterial.image?.path,
                  "cache-bust": __props.coverMaterial.image?.cacheBust,
                  "aspect-ratio": 1/1,
                  "width-on-screen": {'md': 50},
                  alt: `${__props.coverMaterial.name} - ${__props.coverMaterial.materialType}, ${__props.coverMaterial.color.name}`
                }, null, 8, ["image-url", "cache-bust", "alt"])
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.damageImages, (damageImage, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: damageImage,
            class: "cursor-zoom-in",
            onClick: ($event: any) => (openOverlay(_unref(damageImagesIndexOffset) + index))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(_component_ResponsiveImage, {
                    class: "w-full",
                    "width-on-screen": {xs: 100, md: 50},
                    "is-product-image": true,
                    mode: "contain",
                    "image-url": damageImage,
                    "aspect-ratio": 4/3
                  }, null, 8, ["image-url"])
                ])
              ])
            ]),
            _: 2
          }, 1032, ["onClick"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.environmentalImages, (environmentalImage, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: index + (environmentalImage.model.media.model?.url ?? environmentalImage.model.media.model.image.url),
            class: "cursor-zoom-in",
            onClick: ($event: any) => (openOverlay(_unref(environmentalImagesIndexOffset) + index))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_9, [
                _createElementVNode("div", _hoisted_10, [
                  (environmentalImage.model.media.type === _unref(constants).media.vimeoModel)
                    ? (_openBlock(), _createBlock(MediaHandler, {
                        key: 0,
                        "media-block": environmentalImage.model.media,
                        "media-aspect-ratio": { default: 16/9 },
                        "image-width-on-screen": {xs: 100, md: 50},
                        class: "w-full"
                      }, null, 8, ["media-block"]))
                    : (_openBlock(), _createBlock(_component_ResponsiveImage, {
                        key: 1,
                        class: "w-full",
                        "width-on-screen": {xs: 100, md: 50},
                        mode: "contain",
                        "image-url": environmentalImage.model.media.model.url,
                        "cache-bust": environmentalImage.model.media.model.cachebust,
                        "aspect-ratio": 4/3
                      }, null, 8, ["image-url", "cache-bust"]))
                ])
              ])
            ]),
            _: 2
          }, 1032, ["onClick"]))
        }), 128))
      ]),
      _: 3
    }, 8, ["thumbs", "options"]),
    _withDirectives(_createVNode(_component_Swiper, {
      class: "product-detail-swiper product-detail-thumbnails flex justify-start mt-x4 md:max-w-screen-50",
      options: _unref(thumbSwiperOptions),
      onSlideChange: handleThumbChange,
      onToEdge: handleThumbChange,
      onSwiper: setThumbSwiper
    }, {
      default: _withCtx(() => [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.productImages, (image, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: image.maybeAngleImageSku + index,
            class: "thumb-slide",
            onClick: ($event: any) => (_unref(setActiveIndex)(index)),
            onMouseover: ($event: any) => (_unref(setUnderlineAnimation)(index)),
            onMouseout: _unref(removeUnderline)
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_11, [
                _createElementVNode("div", _hoisted_12, [
                  _createVNode(ProductImage, {
                    class: "w-full",
                    sku: image.maybeAngleImageSku,
                    "aspect-ratio": 4/3,
                    lazy: false,
                    "width-on-screen": 1,
                    alt: __props.alt,
                    "cache-bust": image.version,
                    "hide-press-mode-button": true
                  }, null, 8, ["sku", "alt", "cache-bust"])
                ])
              ])
            ]),
            _: 2
          }, 1032, ["onClick", "onMouseover", "onMouseout"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.configuratorImages, (configuratorImage, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: index,
            class: "thumb-slide",
            onClick: ($event: any) => (_unref(setActiveIndex)(index)),
            onMouseover: ($event: any) => (_unref(setUnderlineAnimation)(index)),
            onMouseout: _unref(removeUnderline)
          }, {
            default: _withCtx(() => [
              (configuratorImage.content.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_13, [
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("img", {
                        src: configuratorImage.content,
                        style: {"aspect-ratio":"1/1"}
                      }, null, 8, _hoisted_15)
                    ])
                  ]))
                : _createCommentVNode("", true)
            ]),
            _: 2
          }, 1032, ["onClick", "onMouseover", "onMouseout"]))
        }), 128)),
        (__props.coverMaterial?.image)
          ? (_openBlock(), _createBlock(_component_SwiperSlide, {
              key: "coverMaterial",
              class: "thumb-slide",
              onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(setActiveIndex)(__props.productImages.length))),
              onMouseover: _cache[2] || (_cache[2] = ($event: any) => (_unref(setUnderlineAnimation)(__props.productImages.length))),
              onMouseout: _unref(removeUnderline)
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_16, [
                  _createVNode(_component_ResponsiveImage, {
                    class: "w-full",
                    "width-on-screen": 1,
                    "is-product-image": true,
                    "image-url": __props.coverMaterial.image.path,
                    "cache-bust": __props.coverMaterial.image.cacheBust,
                    "aspect-ratio": 8/7,
                    alt: `${__props.coverMaterial.name} - ${__props.coverMaterial.materialType}, ${__props.coverMaterial.color.name}`,
                    "hide-press-mode-button": true
                  }, null, 8, ["image-url", "cache-bust", "alt"])
                ])
              ]),
              _: 1
            }, 8, ["onMouseout"]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.damageImages, (damageImage, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: damageImage,
            class: "thumb-slide",
            onClick: ($event: any) => (_unref(setActiveIndex)(_unref(damageImagesIndexOffset) + index)),
            onMouseover: ($event: any) => (_unref(setUnderlineAnimation)(_unref(damageImagesIndexOffset) + index)),
            onMouseout: _unref(removeUnderline)
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_17, [
                _createVNode(_component_ResponsiveImage, {
                  class: "w-full",
                  "width-on-screen": 1,
                  "is-product-image": true,
                  mode: "contain",
                  "image-url": damageImage,
                  "aspect-ratio": 4/3,
                  "hide-press-mode-button": true
                }, null, 8, ["image-url"])
              ])
            ]),
            _: 2
          }, 1032, ["onClick", "onMouseover", "onMouseout"]))
        }), 128)),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.environmentalImages, (environmentalImage, index) => {
          return (_openBlock(), _createBlock(_component_SwiperSlide, {
            key: environmentalImage.model.media.model?.url ?? environmentalImage.model.media.model.image.url,
            class: "thumb-slide",
            onClick: ($event: any) => (_unref(setActiveIndex)(_unref(environmentalImagesIndexOffset) + index)),
            onMouseover: ($event: any) => (_unref(setUnderlineAnimation)(_unref(environmentalImagesIndexOffset) + index)),
            onMouseout: _unref(removeUnderline)
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_18, [
                (environmentalImage.model.media.type === _unref(constants).media.vimeoModel)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                      _createElementVNode("div", _hoisted_20, [
                        _createVNode(_component_c_icon, {
                          name: "icon-play",
                          class: "text-white h-10 w-10"
                        })
                      ]),
                      _createVNode(_component_ResponsiveImage, {
                        class: "w-full",
                        "width-on-screen": 1,
                        mode: "contain",
                        "image-url": environmentalImage.model.media.model.image.url,
                        "cache-bust": environmentalImage.model.media.model.image.cachebust,
                        alt: environmentalImage.model.media.model.description,
                        "focus-point": environmentalImage.model.media.model.image.focusPoints,
                        focus: environmentalImage.model.media.model.image.focus,
                        zoom: environmentalImage.model.media.model.image.zoom,
                        "aspect-ratio": 4/3,
                        "original-width": environmentalImage.model.media.model.image.width,
                        "original-height": environmentalImage.model.media.model.image.height,
                        "hide-press-mode-button": true
                      }, null, 8, ["image-url", "cache-bust", "alt", "focus-point", "focus", "zoom", "original-width", "original-height"])
                    ]))
                  : (_openBlock(), _createBlock(_component_ResponsiveImage, {
                      key: 1,
                      class: "w-full",
                      "width-on-screen": 1,
                      mode: "contain",
                      "image-url": environmentalImage.model.media.model.url,
                      "cache-bust": environmentalImage.model.media.model.cachebust,
                      alt: environmentalImage.model.media.model.description,
                      "focus-point": environmentalImage.model.media.model.focusPoints,
                      focus: environmentalImage.model.media.model.focus,
                      "original-width": environmentalImage.model.media.model.width,
                      "original-height": environmentalImage.model.media.model.height,
                      "aspect-ratio": 4/3,
                      "hide-press-mode-button": true
                    }, null, 8, ["image-url", "cache-bust", "alt", "focus-point", "focus", "original-width", "original-height"]))
              ])
            ]),
            _: 2
          }, 1032, ["onClick", "onMouseover", "onMouseout"]))
        }), 128)),
        _createElementVNode("div", {
          class: "underline-container",
          style: _normalizeStyle({transform: `translateX(${thumbX.value}px)`})
        }, [
          _createVNode(DynamicUnderline, {
            "target-width": _unref(targetWidth),
            "target-x-position": _unref(targetXPosition)
          }, null, 8, ["target-width", "target-x-position"])
        ], 4)
      ]),
      _: 1
    }, 8, ["options"]), [
      [_vShow, _unref(showThumbnails)]
    ]),
    _createVNode(VisualizationOverlay, {
      "is-toggled": _unref(isToggled),
      "initial-slide": overlayInitialSlide.value,
      "product-images": __props.productImages,
      "environmental-images": __props.environmentalImages,
      "cover-material": __props.coverMaterial,
      "configurator-images": __props.configuratorImages,
      "damage-images": __props.damageImages,
      onClose: _unref(toggle)
    }, null, 8, ["is-toggled", "initial-slide", "product-images", "environmental-images", "cover-material", "configurator-images", "damage-images", "onClose"])
  ], 64))
}
}

})